import React, { useMemo, useState } from 'react'
import { useAuth } from 'context/authentication/useAuth'
import useApiService from 'services/api'
import { API_HOST } from 'utils/http-client'
import { cn } from 'components/UI/cn'

interface CompanyAvatarProps {
  size?: number
  className?: string
  /**
   * When true, no background fills the container behind the logo image —
   * transparent regions in the logo (e.g. a round logo's corners) pick up the
   * surface the avatar sits on. Use on light surfaces like the profile menu.
   * Default false keeps a `bg-white` tile, which reads cleanly on dark
   * surfaces like the left-nav rail.
   */
  transparentBg?: boolean
}

/**
 * Renders the current user's primary company logo, falling back to the user's
 * initials. Shared between the left-nav profile trigger and the profile menu.
 */
export const CompanyAvatar: React.FC<CompanyAvatarProps> = ({
  size = 32,
  className,
  transparentBg = false,
}) => {
  const { user } = useAuth()
  const api = useApiService()
  const { data: companyData } = api.useGetCompanyDetails()
  // Track the specific URL that failed so a new logo URL retries instead of
  // staying in the failed state forever.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)

  const primaryLogoUrl = useMemo(() => {
    const primary = companyData?.logos?.find((logo) => logo.is_primary)
    if (!primary?.logo_url) return null
    try {
      return new URL(primary.logo_url, API_HOST).href
    } catch {
      return null
    }
  }, [companyData])

  const initials = useMemo(() => {
    const first = user?.first_name?.charAt(0) ?? ''
    const last = user?.last_name?.charAt(0) ?? ''
    const combined = (first + last).toUpperCase()
    return combined || '?'
  }, [user])

  const showImage = !!primaryLogoUrl && failedUrl !== primaryLogoUrl

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center overflow-hidden rounded-md font-bold',
        size >= 40 ? 'text-base' : 'text-sm',
        showImage && !transparentBg && 'bg-white',
        !showImage && 'bg-green-100 text-brand-active',
        className
      )}
      style={{ width: size, height: size }}
    >
      {showImage ? (
        <img
          src={primaryLogoUrl}
          alt="Company logo"
          className="w-full h-full object-contain"
          onError={() => setFailedUrl(primaryLogoUrl)}
        />
      ) : (
        initials
      )}
    </span>
  )
}
